import { UserToggles } from '@ravago/shared/page-data/models/config';
import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';

export interface RfqUomPreference {
   name: string;
   value?: string;
}

interface ResolveRequestForQuotationUomParams {
   channelUOMs: NormalizedUnitOfMeasure[];
   userToggles: UserToggles;
   preferences?: RfqUomPreference[];
   userSelectedUomId?: string;
}

export interface ResolvedRequestForQuotationUom {
   canChangeUom: boolean;
   effectiveChannelUOMs: NormalizedUnitOfMeasure[];
   selectedUomId: string;
   uom: string;
}

export const mapUomIdToUnit = (id: string | undefined): string => {
   if (!id) return 'kg';

   switch (id) {
      case 'imperial':
         return 'lb';
      case 'metric':
         return 'kg';
      default:
         return 'kg';
   }
};

export const mapPreferenceValueToUomId = (value: string | undefined): 'metric' | 'imperial' | undefined => {
   if (value === 'SI metric units') return 'metric';
   if (value === 'US customary units') return 'imperial';
   return undefined;
};

export const createSyntheticCounterpartUom = (uom: NormalizedUnitOfMeasure): NormalizedUnitOfMeasure => {
   const isMetric = uom.name === 'metric';

   return {
      id: isMetric ? 'imperial' : 'metric',
      contentType: uom.contentType,
      name: isMetric ? 'imperial' : 'metric',
      value: isMetric ? 'unit-of-measure.imperial' : 'unit-of-measure.metric',
      default: false
   };
};

export const getDefaultChannelUomId = (uoms: NormalizedUnitOfMeasure[] | undefined): string => {
   if (!uoms || uoms.length === 0) return 'kg';
   const defaultUom = uoms.find((u) => u.default);
   return (defaultUom ?? uoms[0]).id;
};

export const resolveRequestForQuotationUom = ({
   channelUOMs,
   userToggles,
   preferences,
   userSelectedUomId
}: ResolveRequestForQuotationUomParams): ResolvedRequestForQuotationUom => {
   const verifiedPreferenceUomIds = !userToggles.hasVerifiedToggle || !preferences
      ? []
      : preferences
           .filter((preference) => preference.name === 'UOM_SYSTEM')
           .map((preference) => mapPreferenceValueToUomId(preference.value))
           .filter((uomId): uomId is 'metric' | 'imperial' => !!uomId);

   const canVerifiedUserChooseUom = verifiedPreferenceUomIds.includes('metric') && verifiedPreferenceUomIds.includes('imperial');

   const effectiveChannelUOMs =
      userToggles.hasVerifiedToggle && canVerifiedUserChooseUom && channelUOMs.length === 1
         ? [...channelUOMs, createSyntheticCounterpartUom(channelUOMs[0])]
         : channelUOMs;

   const canChangeUom = userToggles.hasVerifiedToggle ? canVerifiedUserChooseUom && effectiveChannelUOMs.length > 1 : channelUOMs.length > 1;

   const availableUomIds = effectiveChannelUOMs.map((uomOption) => uomOption.id);
   const selectedUomId = userSelectedUomId && availableUomIds.includes(userSelectedUomId)
      ? userSelectedUomId
      : getDefaultChannelUomId(effectiveChannelUOMs);

   let uom = mapUomIdToUnit(selectedUomId);

   if (userToggles.hasVerifiedToggle) {
      if (canChangeUom) {
         uom = mapUomIdToUnit(selectedUomId);
      } else if (effectiveChannelUOMs.length === 0) {
         uom = 'kg';
      } else {
         uom = mapUomIdToUnit(verifiedPreferenceUomIds[0]) ?? 'kg';
      }
   }

   return {
      canChangeUom,
      effectiveChannelUOMs,
      selectedUomId,
      uom
   };
};
